import React, { useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { withRouter, Switch, Route } from 'react-router-dom';
import { withTheme } from 'styled-components';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import _isArray from 'lodash/isArray';
import _isNull from 'lodash/isNull';

import Sentry, { captureException } from './utils/sentry';
import { retry } from './utils/utils';
import NavBar from './components/NavBar/NavBar';
import {
  getSolutionSet,
  getTest,
  getSection,
  getBookmarks,
  getServerTime,
  initTest,
  setSecureBrowser,
} from './actions/test';
import ActionTypes from './constants/ActionTypes';
import { getUserType, getRecruiter, getHacker } from './actions/auth';
import InstructionsRoute from './components/Instructions/Instructions.route';
import { setUserName } from './utils/request';
import chatBotConfig, {
  showWelcomeMessage,
  getChatBotExtraParams,
  getCookie,
} from './utils/chatBotConfig';
// import { useTheme } from './context/ThemeContext';
import './utils/ubaInitialiser';

import './App.css';
import { proctoringUBALogger } from './utils/trackingUtil';
import initiateNetworkInfoData from './utils/networkSpeed/networkSpeed';
import { showErrorToast } from './actions/toast';
import TEST_IMG from './assets/img/jpg/speed-test-min.jpg';
import {
  NETWORK_DOWNLOAD_IMG_SIZE_IN_KB,
  SYSTEM_TIME_TRACKING_INTERVAL_IN_MILLISECONDS,
  TEST_TYPE,
  TEST_TYPE_SESSION_STORAGE_KEY,
} from './constants/Constants';
import AutoSubmitModal from './components/shared/AutoSubmitModal/AutoSubmitModal';
import { chatbotConversationName } from './constants/AppConstants';
import Loader from './components/Loader/Loader';
// import { getActiveSession } from './actions/socket';

const AssessmentContainer = React.lazy(() =>
  retry(() => import('./containers/AssessmentContainer'))
);

const SolutionContainer = React.lazy(() =>
  retry(() => import('./containers/Solution/SolutionContainer'))
);
const FeedbackContainer = React.lazy(() =>
  retry(() => import('./containers/FeedbackContainer'))
);
const HelpContainer = React.lazy(() =>
  retry(() => import('./containers/HelpContainer'))
);
const InstructionContainer = React.lazy(() =>
  retry(() => import('./containers/InstructionContainer'))
);
const AIInterview = React.lazy(() =>
  retry(() => import('./components/AIInterview/AIInterview'))
);

const App = ({ match }) => {
  const [conversationName, setConversationName] = useState('');
  const { testslug } = match.params;
  const { initUserDetails, user, appHasError, appError } = useSelector(
    state => state.auth
  );
  const {
    serverTime,
    attemptedProblems,
    testSolutionSet,
    test,
    sections,
    isProctoringABEnabled,
    isAiInterview,
    isSecureBrowserEnable,
  } = useSelector(
    state => ({
      serverTime: state.appStatus?.serverTime,
      attemptedProblems: state.appStatus?.attemptedProblems,
      testSolutionSet: state.testSolutionSet,
      test: state.test,
      sections: state.sections?.sectionsNotGrouped,
      isProctoringABEnabled: state.proctor?.isProctoringABEnabled,
      isAiInterview: state.test?.isAiInterview,
      isSecureBrowserEnable: state?.test?.isSecureBrowserEnable,
    }),
    shallowEqual
  );
  const { user_id, usertype, username } = initUserDetails;
  let userId;
  if (user) userId = user.id;
  const dhwaniProfile = test.interview_avatar;
  // const { isSessionActive } = useSelector(state => state.socket);
  const dispatch = useDispatch();
  // const history = useHistory();
  const serverTimeTrackingIntervalRef = useRef(null);

  const isExtendTestInitialPhase = useMemo(() => {
    const isExtendTestInInitialPhase =
      testSolutionSet?.add_test_duration > 0 &&
      !testSolutionSet?.has_resumed_test;

    return isExtendTestInInitialPhase;
  }, [testSolutionSet]);

  useEffect(() => {
    if (window.nkChatBotUtility) {
      window.nkChatBotUtility.unMountChatBot();
    }
    const isQuizType = window.location.search.includes('testType=quiz');
    if (isQuizType) {
      sessionStorage.setItem(TEST_TYPE_SESSION_STORAGE_KEY, TEST_TYPE.QUIZ);
    }
    // Capture all JS runtime errors
    window.onerror = function(message, source, lineno, colno, error) {
      try {
        window.clarity?.('event', 'js_error', {
          message,
          source,
          line: lineno,
          column: colno,
          stack: error?.stack || '',
        });
      } catch (e) {
        // to do
      }
    };
  }, []);

  useEffect(() => {
    let networkInfoInterval;
    clearInterval(networkInfoInterval);
    if (test.productTypeSmall) {
      networkInfoInterval = initiateNetworkInfoData({
        bandwidthLimitCallback: () =>
          dispatch(showErrorToast('Slow Internet Connection')),
        bandwidthUpperLimit: 4,
        ubaCallback: ({ verbose, label }) =>
          proctoringUBALogger(verbose, label),
        downloadSize: NETWORK_DOWNLOAD_IMG_SIZE_IN_KB,
        TEST_IMG,
        bandwidthThresholdLimit: 1,
        isAlternativeBroadbandIconEnabled: isProctoringABEnabled,
        productTypeSmall: test.productTypeSmall,
      });
    }
  }, [isProctoringABEnabled, test.productTypeSmall]);

  useEffect(() => {
    if (test.name) document.title = test.name;
  }, [test.name]);

  useEffect(() => {
    if (!serverTime) {
      dispatch(getServerTime({ isInitialCall: true }));
    } else {
      // logging user system time
      // Adding it there as proctor related variable also need to be logged
      // so we can not add this logger based on proctorEnable state for now
      // will change the place to proctoring once we can conclude that proctoring
      // setting variables comes correct only in most scenario
      proctoringUBALogger(
        'info',
        `user system time: ${new Date()}, server time: ${new Date(serverTime)}`
      );
    }
  }, [serverTime]);

  useEffect(() => {
    if (
      serverTime &&
      !test.is_expired &&
      testSolutionSet.id &&
      !serverTimeTrackingIntervalRef.current &&
      !isExtendTestInitialPhase
    ) {
      // first servertime call
      dispatch(getServerTime());
      serverTimeTrackingIntervalRef.current = setInterval(() => {
        dispatch(getServerTime());
      }, SYSTEM_TIME_TRACKING_INTERVAL_IN_MILLISECONDS);
    }
  }, [
    serverTime,
    test.is_expired,
    testSolutionSet.id,
    isExtendTestInitialPhase,
  ]);

  useEffect(() => {
    if (testslug && userId)
      try {
        if (window?.clarity && typeof window.clarity === 'function') {
          window.clarity('identify', `${testslug}-${userId}`);
        } else {
          proctoringUBALogger('clarity:error', 'Clarity not loaded yet');
        }
      } catch (err) {
        proctoringUBALogger('clarity:error', err.message);
      }
  }, [testslug, userId]);

  useEffect(() => {
    if (
      test.is_expired &&
      testSolutionSet.id &&
      serverTimeTrackingIntervalRef.current
    ) {
      clearInterval(serverTimeTrackingIntervalRef.current);
      serverTimeTrackingIntervalRef.current = null;
    }
  }, [test.is_expired, testSolutionSet.id]);

  useEffect(() => {
    if (!user_id && serverTime) {
      dispatch(getUserType());
    }
  }, [user_id, dispatch, serverTime]);

  useEffect(() => {
    if (!_isEmpty(usertype) && !userId) {
      if (usertype === 'recruiter') {
        dispatch(getRecruiter());
      } else if (usertype === 'hacker') {
        dispatch(getHacker());
      }
      setUserName(username);
    }
  }, [userId, usertype, dispatch, username]);

  useEffect(() => {
    if (userId) {
      const {
        full_name,
        email,
        username: user_name,
        last_login,
        is_superuser,
      } = user;

      /**
       * Once we have the user details add it
       * to Sentry
       */
      Sentry.setUser({
        email,
        full_name,
        last_login,
        is_superuser,
        user_name,
      });
    }
  }, [userId, user]);

  useEffect(() => {
    if (!test.id && username && testslug) {
      dispatch(getTest(testslug));
    } else if (test.session_state === 'ACTIVE' && username) {
      if (!testSolutionSet.slug) {
        dispatch(getSolutionSet(test.id, false));
      }
      const { isInfraAllocated = false } = getCookie();
      dispatch(
        initTest(user_id, usertype, test.id, test.slug, false, isInfraAllocated)
      );
      dispatch(getSection(test.id));
      dispatch(getBookmarks());
    }
    dispatch(
      setSecureBrowser(
        window.electron && test?.settings?.secure_browser?.enabled
      )
    );
  }, [
    dispatch,
    test.id,
    test.session_state,
    testslug,
    testSolutionSet.slug,
    username,
    user_id,
    usertype,
  ]);

  useEffect(() => {
    if (!_isEmpty(sections) && _isNull(attemptedProblems)) {
      const tempAttemptedProblems = {};
      sections.forEach(section => {
        if (_isArray(section.problems)) {
          section.problems.forEach(problem => {
            if (!_isEmpty(problem.solution))
              tempAttemptedProblems[problem.slug] = true;
            if (problem.children) {
              const solnLength = problem.children
                .map(subProblem => subProblem.solution)
                .filter(item => item).length;
              if (solnLength === problem.children.length)
                tempAttemptedProblems[problem.slug] = true;
            }
          });
        }
      });
      dispatch({
        type: `${ActionTypes.SET_ATTEMPTS_COUNT}_SUCCESS`,
        problems: tempAttemptedProblems,
      });
    }
  }, [attemptedProblems, dispatch, sections]);

  useEffect(() => {
    if (test.name) {
      let needsOnboarding;
      if (test.is_onboarded) {
        needsOnboarding =
          test.settings.hacker_extra_data.custom_data.enabled &&
          !test.is_onboarded;
      } else {
        needsOnboarding =
          !test.is_onboarded && test.settings.hacker_extra_data.profile_data;
      }
      if (
        !needsOnboarding &&
        usertype !== 'recruiter' &&
        user &&
        !isAiInterview
      ) {
        const currentConversationName = test.is_expired
          ? chatbotConversationName.AFTER_TEST
          : testSolutionSet.id
          ? chatbotConversationName.DURING_TEST
          : chatbotConversationName.BEFORE_TEST;

        if (conversationName !== currentConversationName) {
          setConversationName(currentConversationName);
          const params = {
            appName: currentConversationName,
            conversation: currentConversationName,
            ...getChatBotExtraParams(test, user),
          };
          const config = chatBotConfig(params);
          window.nkChatBotUtility.initChatBot(config);
          window.nkChatBotUtility.mountChatBot();

          const cookie = getCookie();
          const { chatbotMounted } = cookie;
          if (chatbotMounted !== 'true' && test.session_state === 'INIT') {
            setTimeout(() => {
              showWelcomeMessage();
            }, 100);
          }
        }
      }
    }
  }, [test, testSolutionSet, user]);

  // const themeToggle = useTheme();
  // useEffect(() => {}, []);

  // This block will be show if and only if the /identity api fails.
  // This API give is details regaring the user who is taking the test
  // If this API fails it means that either there is some problem with
  // the cookie or they are having some internet issue
  if (appHasError === true) {
    const errorEventId = captureException(appError);
    return (
      <section className="system-error">
        <h1>There was an error!</h1>
        <p>
          <button
            type="button"
            onClick={() => Sentry.showReportDialog({ eventId: errorEventId })}
          >
            Report this error
          </button>
        </p>
        <p>
          <button
            type="button"
            onClick={() => {
              window.location.reload(true);
            }}
          >
            Or, try reloading the page
          </button>
        </p>
      </section>
    );
  }
  return (
    <>
      <AutoSubmitModal />
      <NavBar />
      <Switch>
        {isAiInterview && (
          <Route exact path="/test/:testslug/aiinterview">
            <Suspense fallback={<Loader />}>
              <AIInterview
                profileImg={dhwaniProfile}
                isSecureBrowserEnable={isSecureBrowserEnable}
              />
            </Suspense>
          </Route>
        )}
        {!isAiInterview && (
          <Route exact path="/test/:testslug/problems">
            <AssessmentContainer />
          </Route>
        )}
        {!isAiInterview && (
          <Route exact path="/test/:testslug/problem/:problemslug">
            <SolutionContainer />
          </Route>
        )}
        <Route exact path="/test/:testslug/feedback">
          <FeedbackContainer />
        </Route>
        <Route exact path="/test/:testslug/help">
          <HelpContainer />
        </Route>
        <InstructionsRoute exact path="/test/:testslug">
          <InstructionContainer />
        </InstructionsRoute>
      </Switch>
    </>
  );
};

export default withTheme(withRouter(App));
